/**
 * AI Exercise Loader (INT-CMP-010)
 * Manages AI-generated exercises for practice_ai sections.
 *
 * States:
 * - No consent: shows unlock card with button to open consent modal
 * - Has consent, no exercises: shows "Generate" button (user-initiated)
 * - Generating: shows loading spinner
 * - Error: shows error with retry button
 * - Has exercises: renders children + "Generate new" refresh button
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { Box, Text, VStack } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import Ionicons from '../ui/Ionicons';
import { colors, FS_XS, FS_SM } from '../../theme';
import { useLessonStore } from '../../store/lessonStore';
import { selectAiGeneratedExercises, selectIsGeneratingExercises, selectGenerateExercises, selectClearExerciseError, selectExerciseError, selectExerciseErrorCode } from '../../store/lessonSelectors';
import { useAIConsent } from '../../hooks/useAIConsent';
import { AIConsentModal } from '../AIConsentModal';
import type { AiAgentInstructions, GeneratedExercise } from '../../types/lesson';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface AIExerciseLoaderProps {
  lessonSlug: string;
  instructions: AiAgentInstructions;
  children: (exercises: GeneratedExercise[]) => React.ReactNode;
}

export const AIExerciseLoader = React.memo(function AIExerciseLoader({
  lessonSlug,
  instructions,
  children,
}: AIExerciseLoaderProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const aiExercises = useLessonStore(selectAiGeneratedExercises);
  const isGenerating = useLessonStore(selectIsGeneratingExercises);
  const generateExercises = useLessonStore(selectGenerateExercises);
  const clearExerciseError = useLessonStore(selectClearExerciseError);
  const error = useLessonStore(selectExerciseError);
  const errorCode = useLessonStore(selectExerciseErrorCode);
  const { hasConsent, needsReConsent, grantConsent } = useAIConsent();

  const [showConsentModal, setShowConsentModal] = useState(false);

  const needsConsent = !hasConsent || needsReConsent;

  // If server rejects with AI_CONSENT_REQUIRED, show consent modal
  // Intentional: reacting to external error code by showing modal
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (errorCode === 'AI_CONSENT_REQUIRED') {
      clearExerciseError();
      setShowConsentModal(true);
    }
  }, [errorCode, clearExerciseError]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleGenerate = useCallback(() => {
    generateExercises(lessonSlug, instructions);
  }, [generateExercises, lessonSlug, instructions]);

  const handleConsentAccept = useCallback(async () => {
    const granted = await grantConsent();
    // Always dismiss so the failure banner (from useAIConsent) isn't
    // covered by the native Modal. Skip auto-generate on failure —
    // it would 403 with AI_CONSENT_REQUIRED and re-trigger the
    // consent-modal flow, hiding the banner.
    setShowConsentModal(false);
    if (!granted) return;
    generateExercises(lessonSlug, instructions);
  }, [grantConsent, generateExercises, lessonSlug, instructions]);

  const handleConsentDecline = useCallback(() => {
    setShowConsentModal(false);
  }, []);

  const handleRetry = useCallback(() => {
    clearExerciseError();
    generateExercises(lessonSlug, instructions);
  }, [clearExerciseError, generateExercises, lessonSlug, instructions]);

  const consentModal = (
    <AIConsentModal
      visible={showConsentModal}
      onAccept={handleConsentAccept}
      onDecline={handleConsentDecline}
    />
  );

  const buttonHeight = Math.round(44 * fontScale);

  // ── No consent: unlock card ──────────────────────────────────────────
  if (needsConsent && !aiExercises) {
    return (
      <>
        <Box
          p="$4"
          rounded="$lg"
          style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
        >
          <VStack space="sm" alignItems="center">
            <Ionicons name="lock-closed-outline" size={32 * fontScale} color={theme.textSecondary} />
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}
            >
              {t('lessons.aiConsentRequired')}
            </Text>
            <Pressable
              testID="ai-exercise-loader-consent-button"
              accessibilityLabel={t('lessons.enableAIExercises')}
              accessibilityRole="button"
              onPress={() => setShowConsentModal(true)}
              style={{
                marginTop: 4,
                minHeight: buttonHeight,
                paddingHorizontal: 20,
                borderRadius: buttonHeight / 2,
                backgroundColor: colors.primary,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: '#FFFFFF', fontWeight: '600', ...ffs }}
              >
                {t('lessons.enableAIExercises')}
              </Text>
            </Pressable>
          </VStack>
        </Box>
        {consentModal}
      </>
    );
  }

  // ── Generating: loading spinner ──────────────────────────────────────
  if (isGenerating) {
    return (
      <Box
        p="$6"
        rounded="$lg"
        alignItems="center"
        style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
      >
        <VStack space="md" alignItems="center">
          <ActivityIndicator size="large" color={colors.primary} />
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
          >
            {t('lessons.generatingExercises')}
          </Text>
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
          >
            {t('lessons.generatingExercisesSubtext')}
          </Text>
        </VStack>
      </Box>
    );
  }

  // ── Error: retry button ──────────────────────────────────────────────
  if (error && !aiExercises) {
    return (
      <>
        <Box
          p="$4"
          rounded="$lg"
          style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
        >
          <VStack space="sm" alignItems="center">
            <Text allowFontScaling={false} style={{ fontSize: 32 }}>
              {'\u26A0\uFE0F'}
            </Text>
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}
            >
              {t('lessons.exerciseGenerationError')}
            </Text>
            <Pressable
              onPress={handleRetry}
              testID="ai-exercise-loader-retry-button"
              accessibilityLabel={t('common.retry')}
              accessibilityRole="button"
              style={{
                marginTop: 8,
                minHeight: buttonHeight,
                paddingHorizontal: 20,
                borderRadius: buttonHeight / 2,
                backgroundColor: colors.primary,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: '#FFFFFF', fontWeight: '600', ...ffs }}
              >
                {t('common.retry')}
              </Text>
            </Pressable>
          </VStack>
        </Box>
        {consentModal}
      </>
    );
  }

  // ── Has consent, no exercises yet: generate button ───────────────────
  if (!aiExercises || aiExercises.length === 0) {
    return (
      <>
        <Box
          p="$4"
          rounded="$lg"
          style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
        >
          <VStack space="sm" alignItems="center">
            <Ionicons name="sparkles-outline" size={32 * fontScale} color={colors.primary} />
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}
            >
              {t('lessons.generateExercisesPrompt')}
            </Text>
            <Pressable
              testID="ai-exercise-loader-generate-button"
              accessibilityLabel={t('lessons.generateExercisesButton')}
              accessibilityRole="button"
              onPress={handleGenerate}
              style={{
                marginTop: 4,
                minHeight: buttonHeight,
                paddingHorizontal: 20,
                borderRadius: buttonHeight / 2,
                backgroundColor: colors.primary,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: '#FFFFFF', fontWeight: '600', ...ffs }}
              >
                {t('lessons.generateExercisesButton')}
              </Text>
            </Pressable>
          </VStack>
        </Box>
        {consentModal}
      </>
    );
  }

  // ── Has exercises: render children + refresh button ──────────────────
  return (
    <>
      {children(aiExercises)}
      <View style={{ alignItems: 'center', marginTop: 12 }}>
        <Pressable
          testID="ai-exercise-loader-refresh-button"
          accessibilityLabel={t('lessons.generateNewExercises')}
          accessibilityRole="button"
          onPress={handleGenerate}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            paddingVertical: 10,
            paddingHorizontal: 16,
            borderRadius: 20,
            borderWidth: 1,
            borderColor: theme.border,
            backgroundColor: theme.card,
          }}
        >
          <Ionicons name="refresh-outline" size={16 * fontScale} color={theme.primary} />
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_SM * fontScale, color: theme.primary, fontWeight: '500', ...ffs }}
          >
            {t('lessons.generateNewExercises')}
          </Text>
        </Pressable>
      </View>
      {consentModal}
    </>
  );
});
